<template>
  <div id="app" :data-theme="theme">
    <mobile-screen v-if="!shouldShowResponsiveLayout" />
    <div v-else>
      <div v-if="isOfacFlag">
        <Ofac
          :ofacDocumentation="ofacDocumentation"
          :supportEmail="supportEmail"
          :brandLogo="brandLogo"
        />
      </div>
      <div v-else-if="isForceResetPasswordFlag">
        <ForceResetPassword :brandLogo="brandLogo" :brandName="brandName" />
      </div>
      <!-- incase of not logged-in then `isAuthProtectedRoute` true and vue-router open corresponding route -->
      <!-- incase of logged-in we wait for `isInitCPStepsDone` to be truthy to make sure all necessary data is set in app store -->
      <div v-else-if="isInitCPStepsDone || !isAuthProtectedRoute">
        <div v-if="authenticated">
          <app-header
            :brandDetails="brandDetails"
            :logout="logout"
            :imageUrl="imageUrl"
            v-if="!view.hideAppHeader && !isMobile"
          />

          <div v-if="!view.hideAppHeader && isMobile">
            <app-header-mobile
              :brandDetails="brandDetails"
              :imageUrl="imageUrl"
              :logout="logout"
              :type="DOMAIN_SWITCHER_TYPES.MOBILE"
            />
          </div>

          <!-- once you fix above TODO make sure to set if-else order -->
          <neo-welcome-screen
            v-if="brandDetails.showWelcomePopup && isFromNeo"
            @onClosePopup="isFromNeo = false"
          />

          <!-- application banner - mail | DNS | site -->
          <AppBanner
            :paymentEnabled="paymentEnabled"
            :getDnsAndEmailMedusaAttrs="getDnsAndEmailMedusaAttrs"
            :addons="addons"
            @redirectToPlanUpgrade="redirectToPlanUpgrade"
          />
        </div>
        <div
          :class="{
            main: true,
            'non-iframe-widget': inWidgetModeWithoutIframe,
          }"
        >
          <SideNav
            v-if="authenticated && isInitCPStepsDone"
            :addons="addons"
            :brandDetails="brandDetails"
            :paymentEnabled="paymentEnabled"
            :titanMaxEnabled="titanMaxEnabled"
            :view="view"
            :imageUrl="imageUrl"
            ref="appView"
          />
          <div
            class="router-container"
            :class="
              isAppBannerVisible && authenticated && !view.hideSideBar
                ? 'view increase-padding-container'
                : authenticated
                  ? view.name !== 'defaultView'
                    ? `${view.name}View`
                    : 'view'
                  : ''
            "
          >
            <div
              v-if="
                view &&
                view.showModalAsPage &&
                $route.path !== view.initialRoute &&
                $route.name !== view.home &&
                showExtraStuffInFooter
              "
              class="widgetViewContainer"
            >
              <widget-breadcrumb-nav
                :key="$route.path"
                v-if="
                  !(view && view.flags && view.flags.hideBreadcrumb) && $route.name !== view.home
                "
              />
              <!-- <h4 v-else-if="$route.name === view.home">
                {{ $t("emailAccounts.createEmail.emailLabel") }}
                {{ domainName !== "" ? "@" + domainName : "" }}
              </h4> -->
              <a
                :class="['help-link-icon', { rightAlignIcon: $route.path === '/' }]"
                target="_blank"
                v-if="!hideIFrameHelpButton"
                :href="brandDetails.adminGuide"
              />
            </div>
            <router-view
              @onLoginSuccess="onLoginSuccess"
              :setOfacParams="setOfacParams"
              :setForceResetPasswordParams="setForceResetPasswordParams"
              @fetchAddons="fetchAddons"
              @fetchCardDetails="fetchCardDetails"
              @fetchCurrentPlan="syncCurrentPlan"
              @fetchEmailAccounts="fetchEmailAccounts"
              @fetchPaymentURLDetails="fetchPaymentURLDetails"
              @pageLoaded="pageLoaded"
              :authState="authenticated ? authState.AUTH : authState.NO_AUTH"
              :addons="addons"
              :brandDetails="brandDetails"
              :currency="currency"
              :domainNotFound="domainNotFound"
              :getOtherMedusaAttrb="getDnsAndEmailMedusaAttrs"
              :onDNSLookupSuccess="onDNSLookupSuccess"
              :partnerId="partnerId"
              :partnerURLs="partnerURLs"
              :titanMaxEnabled="titanMaxEnabled"
              :trials="trials"
              :view="view"
            />
            <div
              class="powered-by-titan"
              v-if="view.showPoweredByTitan && $route.path !== '/' && showExtraStuffInFooter"
            >
              <img alt="powered by" src="./assets/powered-by-titan-grey.svg" />
            </div>
          </div>
        </div>
        <language-switcher v-if="!authenticated && isShowSwitcher"></language-switcher>
      </div>
      <Loader v-else />
    </div>
    <widget-toast-screen />
    <common-modal />
  </div>
</template>

<script>
import AppHeaderMobile from "@/components/Utility/AppHeaderMobile.vue";
import CommonModal from "@/components/Utility/CommonModal.vue";
import DomainSwitcher from "@/components/Utility/DomainSwitcher.vue";
import { isReferralEnabled } from "@/helpers/referral.helper";
import { DOMAIN_SWITCHER_TYPES, switchDomain } from "@/helpers/domainHelpers";
import { openNeoSiteBuilderForBundle } from "@/helpers/neoWebsiteHelpers";
import { addPlanTrialInfo, isBundleEligibleForLoad } from "@/helpers/plansHelpers";
import bundleService from "@/services/bundle/bundle.service";
import bundleEvents, { preAuthBundleEvents } from "@/telemetry/bundleEvents";
import brand from "./helpers/brand";
import {
  ADDONS_ON_TRIAL,
  CONTENT_DIMENSION_DID_UPDATE,
  EMAIL_ACCOUNTS_CHANGED,
  IFRAME_WIDTH_CHANGED,
  bindCurrencySymbol,
  clearAuthSession,
  formatDate,
  getCookie,
  getCurrencySymbol,
  getFontForPartner,
  isAppLoadedInsideIframe,
  isMobile,
  sendPostMessageToParentWindow,
  setCookie,
  setWebsiteMetaData,
  setViewAndTheme,
  getJWTpayloadData,
  getFilteredQueryParams,
  hardRedirect,
  getAppSource,
  shouldShowResponsiveCP,
  setRootAuthHeaders,
  isNeoCustomDomain,
  parseDnsRecords,
  getCurrentUserEmail,
  smartStorage,
  getAuthToken,
  getExternalAuthToken,
  getDomainName,
  isNeoBrand,
  returnObjectIfValueExists,
  getPricingConfigIdsForProducts,
} from "./helpers";
import viewTypes from "./views";
import eventBus from "./helpers/event-bus";
import throttle from "lodash/throttle";
import { mapGetters, mapActions } from "vuex";
const AppHeader = () => import("./components/Utility/AppHeader.vue");
const AppBanner = () => import("./components/Banner/AppBanner.vue");
const SideNav = () => import("./components/SidebarNav/SideNav.vue");
const Login = () => import("./components/Auth/Login.vue");
const LanguageSwitcher = () => import("./components/Utility/LangSwitcher.vue");
const Ofac = () => import("./components/Ofac/Ofac.vue");
const ForceResetPassword = () => import("./components/ForceResetPassword/ForceResetPassword.vue");
const Loader = () => import("./components/Utility/Loader.vue");
const MobileScreen = () => import("./components/MobileScreen/MobileScreen.vue");
import { loadCurrentLanguageFile } from "./i18n";
import { getCommonMedusaParams } from "@/telemetry/medusaUtils";
import DomainAdminWelcomeScreen from "@/components/Auth/DomainAdminWelcomeScreen.vue";
import NeoWelcomeScreen from "@/components/Auth/NeoWelcomeScreen.vue";
import {
  AUTH_STATE,
  ENTRI_SETUP_MODE,
  ERROR_BANNER_TYPE,
  FLOW_TYPES,
  LOGIN_TYPE,
  ROUTE_ACTIONS,
  ROUTE_ACTIONS_INTERNAL_FLAG,
  ROUTE_ACTIONS_PROPS,
} from "./helpers/const";
import { ENTRI_SETUP_TYPE } from "./helpers/entriConfig";
import { getEntriSetupUrl, isSiteSetupMode } from "./helpers/entri";
import { measureInitialAppLoad } from "../src/analytics/performance/pageTimings";
import queryParamStore from "./global/queryParamStore";
import emailService from "@/services/email/email.service";
import billingService from "@/services/billing/billing.service";
import entriService from "@/services/entri/entri.service";
import authService from "@/services/auth/auth.service";
import { initGtm } from "@/global/lib/gtm";
import Logger from "./helpers/logger";
import {
  formatDomainDetails,
  formatProductDetails,
  getOrderDetailsOfProduct,
  getProductsFromCurrentPlan,
  hasDomainProduct,
  hasMailSuiteProduct,
  hasSiteProduct,
  PRODUCTS,
} from "./helpers/product";
import { initAuthLibraries } from "./global";
import { getAuthContextForLibs } from "./helpers/lib.helper";
import referralService from "./services/referral/referral.service";
import {
  getAppAction,
  getAppActionProps,
  hasMonthExtensionActionParam,
} from "./helpers/actionHelpers";
import { getCurrentBundleDetails } from "./helpers/bundle";
import { isOrderSuspended } from "@/helpers/billingHelpers";

let observedElementDetails = { element: null, observer: null };

export default {
  name: "App",
  components: {
    AppBanner,
    DomainSwitcher,
    AppHeaderMobile,
    CommonModal,
    AppHeader,
    LanguageSwitcher,
    Loader,
    Login,
    MobileScreen,
    Ofac,
    ForceResetPassword,
    SideNav,
    DomainAdminWelcomeScreen,
    NeoWelcomeScreen,
  },
  data() {
    return {
      addons: [],
      authenticated: false,
      brandDetails: brand.getDetails(),
      brandLogo: null,
      brandName: "",
      cr: bindCurrencySymbol.bind(this),
      enableDNSVerification: false,
      formatDate,
      isFetching: true,
      failedToInitCP: false,
      hideIFrameHelpButton: false,
      imageUrl: "",
      isForceResetPasswordFlag: false,
      isFromNeo: false,
      isMobile: isMobile(),
      isOfacFlag: false,
      isShowSwitcher: false,
      isTitanMail: false,
      loginType: null,
      lookupData: null,
      ofacDocumentation: "",
      partnerId: null,
      partnerURLs: {},
      paymentEnabled: false,
      rebrandedToTitan: false,
      showExtraStuffInFooter: false,
      supportEmail: "",
      theme: null,
      shouldShowResponsiveLayout: true,
      isEntriDetailsFetched: false,
      authState: AUTH_STATE,
    };
  },
  computed: {
    DOMAIN_SWITCHER_TYPES() {
      return DOMAIN_SWITCHER_TYPES;
    },
    ...mapGetters([
      "locale",
      "trials",
      "view",
      "showDomainAdminPopup",
      "partnerInfo",
      "products",
      "isAppBannerVisible",
    ]),
    ...mapGetters({
      mailOrderId: "mail/orderId",
      domainName: "domain/domainName",
      domainConnectData: "domain/domainConnectData",
      domainOwnershipVerified: "domain/domainOwnershipVerified",
      domainMXRecordVerified: "domain/domainMXRecordVerified",
      domainSpfVerified: "domain/domainSpfRecordVerified",
      domainVerifiedAndConnected: "domain/domainVerifiedAndConnected",
      domainDkimVerified: "domain/domainDkimVerified",
      domainARecordVerified: "domain/domainARecordVerified",
      domainCNameRecordVerified: "domain/domainCNameRecordVerified",
      siteVerifiedAndConnected: "domain/siteVerifiedAndConnected",
      createdEmailAccounts: "currentDomainMailboxDetails",
      mailPlanList: "mail/plans",
      sitePlanList: "site/plans",
      domainPlanList: "domain/plans",
      currentPlan: "currentPlanDetails",
      currentPlanMailDetails: "mail/currentProductDetails",
      isFreeMailPlan: "mail/isFreePlan",
      isFreeSitePlan: "site/isFreePlan",
      mailPlanType: "mail/currentPlanType",
      externalPayment: "payment/externalPayment",
      hideBilling: "payment/hideBilling",
      bundleList: "bundle/bundleList",
      isMailExpired: "mail/isExpired",
      entriSetupPossible: "entri/setupPossible",
      bundleStatus: "bundle/status",
      isCurrentPlanFetched: "isCurrentPlanFetched",
    }),
    isBundleSuspended() {
      return isOrderSuspended(this.bundleStatus);
    },
    isInitCPStepsDone() {
      return (
        // this is to make sure all sub routes has access to store values
        // so we will wait until data is being fetched and set.
        // `isCurrentPlanFetched` (and not `orderDetails`) is the check here because the older
        // `getCurrentPlan` response shape carries no `orderDetails` — see `isLatestAPIResp` in
        // billingHelpers — and those plans would otherwise never open this gate.
        !this.isFetching && (this.isCurrentPlanFetched || this.failedToInitCP)
      );
    },
    isAuthProtectedRoute() {
      // Intentionally independent of `authenticated`: `initCP` flips that flag well before the
      // current plan reaches the store, so including it here would let sub routes mount against
      // empty getters (medusa events resolving to dummy_entity_id, plan reads returning undefined).
      return this.$route.meta.requiresAuth;
    },
    domainNotFound() {
      return this.domainConnectData?.DomainNotFound;
    },
    inWidgetModeWithoutIframe() {
      return this.view.name === "widget" && !isAppLoadedInsideIframe();
    },
    titanMaxEnabled() {
      return !!this.addons.find(a => this.view?.addonsWithFakedoor?.includes(a.type));
    },
    currency() {
      const currencyCode = this.currentPlan?.currency;
      return getCurrencySymbol(currencyCode);
    },
  },
  methods: {
    ...mapActions({
      setDomainConnectData: "domain/domainConnectData",
    }),
    onIframeDimensionChange: throttle(
      function (event) {
        const eventName = event.data.event;
        if (eventName === IFRAME_WIDTH_CHANGED) {
          const element = document.getElementById("app");
          sendPostMessageToParentWindow(CONTENT_DIMENSION_DID_UPDATE, {
            height: element.scrollHeight,
            width: element.scrollWidth,
          });
        }
      },
      2000,
      { trailing: true }
    ),
    observeChangesInDOM(element) {
      const observer = new ResizeObserver(
        throttle(
          entries => {
            for (let entry of entries) {
              const element = entry.contentRect;
              sendPostMessageToParentWindow(CONTENT_DIMENSION_DID_UPDATE, {
                height: element.height,
                width: element.width,
              });
            }
          },
          2000,
          { trailing: true }
        )
      );
      observer.observe(element);
      return { element, observer };
    },
    /**
     * Triggers an analytics event to Medusa with the login information.
     */
    triggerLoginEvent() {
      var eventObj = {
        eventName: "control_panel_logged_in",
        auto_login: this.loginType !== LOGIN_TYPE.LOGIN_WITH_CREDS,
        cp_type: isAppLoadedInsideIframe() ? "iframe" : "normal",
        browser_locales: navigator.languages,
        titan_locale: document.documentElement.getAttribute("lang"),
        partner_id: this.partnerId,
        ...returnObjectIfValueExists(
          "free_month_eligible",
          // this is expected, we just pass whether the month ext param present or not
          // we don't actually check for eligibility in medusa event
          hasMonthExtensionActionParam()
        ),
      };
      this.medusaEvents.send(
        this.medusaEvents.createEventObj(eventObj, this.domainName, "flock_order")
      );
      bundleEvents.logCPLoggedInEvent({
        ...eventObj,
        cp_locale: document.documentElement.getAttribute("lang"),
      });
    },
    async onLoginSuccess(loginType, partnerId) {
      this.setViewAndTheme();
      this.loginType = loginType;
      this.partnerId = partnerId;
      await this.initCP();
      this.triggerLoginEvent();
    },
    pageLoaded(flag) {
      this.showExtraStuffInFooter = flag;
    },
    /**
     * Sets the user's locale to the domain set by backend.
     * Locale preference is in the following order:
     * 1. locale parameter from the query string.
     * 2. locale parameter from panel/domain/list response
     * 3. locale from JWT (possibly deprecated)
     * 4. locale from browser
     * The locale from JWT is deprecated as backend folks claim there was never any locale in JWT.
     * @param {String} domainLocale - the locale of the domain response
     */
    setLocale(domainLocale) {
      const { locale: queryLocale, jwt: token } = this.$route.query;
      const { locale: jwtLangLocale } = token ? getJWTpayloadData(token) : {};
      loadCurrentLanguageFile(queryLocale || domainLocale || jwtLangLocale || navigator.language);
    },
    setViewAndTheme() {
      const viewAndThemeObj = setViewAndTheme(
        this.$route.query.view,
        this.$route.meta.view,
        this.$route.query.customTheme,
        this.$route.meta.customTheme
      );
      this.$store.dispatch("view", viewTypes[viewAndThemeObj.view]);
      this.theme = viewAndThemeObj.theme;
      this.$store.dispatch("theme", this.theme);
      getFontForPartner(this.theme);
      if (this.view.hideScrollbar) {
        document.body.classList.add("without-scrollbar");
        document.documentElement.style.scrollbarWidth = "none"; // Firefox
      }
    },
    setOfacParams(ofacObj) {
      const { isOfacFlag, brandLogo, supportEmail, ofacDocumentation } = ofacObj;
      this.isOfacFlag = isOfacFlag;
      this.brandLogo = brandLogo;
      this.supportEmail = supportEmail;
      this.ofacDocumentation = ofacDocumentation;
    },
    setForceResetPasswordParams(forceResetPasswordObj) {
      const { isForceResetPasswordFlag, brandLogo, brandName } = forceResetPasswordObj;
      this.isForceResetPasswordFlag = isForceResetPasswordFlag;
      this.brandLogo = brandLogo;
      this.brandName = brandName;
    },
    getDnsAndEmailMedusaAttrs() {
      const otherInfo = this.lookupData
        ? {
            mx: (this.lookupData.mxRecords || []).join(" , "),
            spf: (this.lookupData.spfRecords || []).join(" , "),
            ns: (this.lookupData.nsRecords || []).join(" , "),
            a: (this.lookupData.aRecords || []).join(" , "),
            domainconnect: this.lookupData.isDomainConnectDomain,
          }
        : {};

      return {
        adminEmail: getCurrentUserEmail(),
        no_of_pop_email_created: this.createdEmailAccounts?.length || 0,
        ...otherInfo,
      };
    },
    handleEntriSetupRedirection(setupType, setupFor) {
      const isAutomaticSetupType = setupType === ENTRI_SETUP_TYPE.AUTOMATIC;

      // medusa events
      const commonMedusaAttribute = {
        auto_domain_setup_supported: this.entriSetupPossible,
        connection_method: setupType,
      };

      if (!isSiteSetupMode(setupFor)) {
        this.medusaEvents.trackEvent({
          eventName: "mx_banner_clicked",
          source: getAppSource(),
          ...getCommonMedusaParams(),
          ...this.getDnsAndEmailMedusaAttrs(),
          ...commonMedusaAttribute,
        });
      }

      // if Entri Automatic setup possible
      if (isAutomaticSetupType) {
        const dnsSetupUrl = this.partnerInfo?.urls?.dnsRecordSetupUrl;
        const domainName = this.domainName;
        const domainId = this.mailOrderId;
        hardRedirect(
          getEntriSetupUrl({
            setupFor,
            dnsSetupUrl,
            domainName,
            domainId,
          }),
          true,
          true
        );
      }
    },
    redirectToPlanUpgrade(bannerType, action) {
      const isNeoPaymentFailure =
        ERROR_BANNER_TYPE.SUBSCRIPTION_EXPIRED_PAYMENT_FAILURE === bannerType;

      if (isNeoPaymentFailure) {
        // open payment card section
        queryParamStore.set(ROUTE_ACTIONS_INTERNAL_FLAG.OPEN_RENEW_FLOW_CARD_SECTION, true);
      }

      const queryParams = {
        [action]: true,
      };

      // Add chargeCancelledOrders flag when bundle is suspended and it's a renew flow
      if (this.isBundleSuspended && action === FLOW_TYPES.RENEW_BUNDLE) {
        queryParams[FLOW_TYPES.CHARGE_CANCELLED_ORDERS] = true;
      }

      this.$refs.appView.getJWTpayloadForUpgrade(
        this.isMailExpired ? "/billing-and-upgrade" : "/site-billing-and-upgrade",
        true,
        queryParams
      );
    },
    authenticateExternalToken() {
      const externalToken = getExternalAuthToken();
      return authService
        .authenticateExternalToken(this.domainName, externalToken)
        .then(this.onExtAuthSuccess)
        .catch(this.onExtAuthFailed);
    },
    onExtAuthSuccess(data) {
      if (data) {
        this.initCP();
      }
    },
    onExtAuthFailed(response) {
      if (response && response.data && response.data.desc) {
        this.logout();
        this.showToast(response.data.desc);
      }
    },
    isCurrentPlanEligibleForLoad(currentPlanDetails) {
      const siteOrder = currentPlanDetails.orderDetails?.[PRODUCTS.SITE];
      const mailSuiteOrder = currentPlanDetails.orderDetails?.[PRODUCTS.MAIL_SUITE];
      return isBundleEligibleForLoad(siteOrder, mailSuiteOrder);
    },
    handleFreeProvisionedSiteRedirection(bundleList, currentBundle) {
      const nextPossibleBundle = bundleList.find(bundle => {
        const siteOrder = getOrderDetailsOfProduct(bundle.orders, PRODUCTS.SITE);
        const mailSuiteOrder = getOrderDetailsOfProduct(bundle.orders, PRODUCTS.MAIL_SUITE);
        // Check if the bundle.orders does NOT contain an order with type 'Site' and planType 'free_site' | 'freeplan_site'
        return isBundleEligibleForLoad(siteOrder, mailSuiteOrder);
      });

      if (nextPossibleBundle) {
        // switch to the next available domain that is not a free site bundle
        switchDomain(nextPossibleBundle);
      } else {
        const { id: bundleId } = currentBundle;
        const bundleToSwitchTo = bundleList.find(bundle => bundle.id === bundleId);
        // redirect to SiteBuilder
        openNeoSiteBuilderForBundle(bundleToSwitchTo, true);
      }
    },
    async initCP() {
      try {
        this.failedToInitCP = false;
        setRootAuthHeaders.call(this);

        await Promise.all([this.fetchPartnerInfo(), this.getAllBundles()]);

        initAuthLibraries(getAuthContextForLibs(), {
          shouldShowReferralLink: this.$store.getters["referral/shouldShowReferralLink"],
        });

        if (isReferralEnabled(this.partnerInfo.partnerState)) {
          await referralService.getCelloRewardDetails();
        }

        this.authenticated = true;

        const currentPlanData = await this.fetchCurrentPlan();

        this.setCurrentBundleDetails(currentPlanData);
        const currentBundle = this.getCurrentBundleDetails(currentPlanData);

        if (!this.isCurrentPlanEligibleForLoad(currentPlanData)) {
          this.handleFreeProvisionedSiteRedirection(this.bundleList, currentBundle);
        }

        const products = getProductsFromCurrentPlan(currentPlanData);
        const pricingConfigIds = getPricingConfigIdsForProducts(currentPlanData);

        // `setCurrentPlanDetails` reads the plan catalogs through `getMailPlanDetails` and friends,
        // so `fetchPlans` has to land first — it is the only fetch below that it depends on.
        await this.fetchPlans(currentPlanData?.currency, pricingConfigIds);

        // The plan goes into the store before anything else is fetched, because the events those
        // fetches report (`mx_banner_shown`, `mail_connect_domain_completed` / `_failed`) resolve
        // their entityId from `mail/orderId`. Fetch first and they go out with `dummy_entity_id`.
        this.setCurrentPlanDetails(currentPlanData, products);

        // Fire-and-forget, kept ahead of the fetches below so its lookup data has as long as
        // possible to arrive before `mx_banner_shown` reports the MX/SPF attrs.
        this.fetchDnsDetails();

        await Promise.all([this.fetchEmailAccounts(products), this.fetchAddons()]);

        this.handleInternalRedirection();

        this.hideAppLoader();

        await this.fetchPaymentURLDetails();
        this.fetchCardDetails();
      } catch (e) {
        Logger.error("Error in `initCP`", e);
        this.failedToInitCP = true;
        this.hideAppLoader();
      }
    },
    fetchPartnerInfo() {
      return this.getPartnerInfo().then(this.onSuccessPartnerFetch).catch(this.onErrorPartnerFetch);
    },
    fetchPlans(currency, pricingConfigIds) {
      return billingService
        .getAllPlans(currency || this.currentPlan.currency, {
          pricingConfigIds,
        })
        .then(this.onFetchPlanSuccess)
        .catch(this.forceLogout);
    },
    syncAllProductPlans() {
      const pricingConfigIds = getPricingConfigIdsForProducts(this.currentPlan);
      return this.fetchPlans(this.currentPlan?.currency, pricingConfigIds);
    },
    getCurrentBundleDetails(currentPlan) {
      return getCurrentBundleDetails(this.bundleList, currentPlan);
    },
    getMailPlanDetails(planId) {
      return this.mailPlanList.find(p => p.id === planId);
    },
    getSitePlanDetails(planId) {
      return this.sitePlanList.find(p => p.id === planId);
    },
    getDomainPlanDetails(planId) {
      return (
        this.domainPlanList.find(p => p.id === planId) || this.getMailPlanDetails(planId)
        // fallback is for old orders incase plan is not present in domainPlanList then check in mail plans
      );
    },
    forceLogout(e) {
      // in case the app is not inside an iframe, log the user out.
      // if it's loaded inside an iframe, no need to do anything as the
      // partner side handles data transfer to the iframe
      if (!isAppLoadedInsideIframe()) {
        this.logout();
      } else {
        this.$router.replace({ name: "LoginFailureScreen" });
      }

      this.$store.dispatch("mail/plans", []);
      this.$store.dispatch("site/plans", []);
      return Promise.reject(e);
    },
    onFetchPlanSuccess(productPlans) {
      return Promise.resolve(productPlans);
    },
    async logout() {
      var eventObj = {
        eventName: "control_panel_logout_button_click",
      };
      /* Pre-auth like its `logCPLoggedOutEvent` sibling below: `logout` is not only the user's
         logout click, it is also the force-logout path (`forceLogout`, `onExtAuthFailed`, and a 401
         that already ran `clearAuthSession` via `go-to-login`), so the token is frequently gone by
         the time this fires and `/m/addEvent` rejects it. */
      this.medusaEvents.sendPreAuthEvent(
        this.medusaEvents.createEventObj(eventObj, this.domainName, "flock_order")
      );
      try {
        await authService.logout();
        /* The token is already cleared by `logout`, so this event has to go out as pre-auth. */
        preAuthBundleEvents.logCPLoggedOutEvent();
        this.authenticated = false;
        this.$router.push({ name: "Login" });
        if (this.loginType === LOGIN_TYPE.LOGIN_WITH_CREDS) {
          // this is to avoid branding issues
          window.location.reload();
        }
      } catch (e) {
        console.error("Error in logout", e);
        this.showToast("Something went wrong while logging out. Please try again later.");
      }
    },
    onErrorPartnerFetch(e) {
      this.isTitanMail = brand.deriveBrandFromURL() === brand.NAMES.TITAN;
      this.setBrandDetails();
      setWebsiteMetaData.call(this);
      return Promise.reject(e);
    },
    onSuccessPartnerFetch(data) {
      this.partnerId = data.partnerId;
      this.$store.dispatch("partnerId", data.partnerId);
      this.$store.dispatch("partnerName", data.companyName);
      this.$store.dispatch("theme", this.$route.query.customTheme || data.resources?.cpThemeId);
      this.$store.dispatch("partnerInfo", data);
      this.supportEmail = data.emails && data.emails.supportEmail;
      this.ofacDocumentation = data && data.urls && data.urls.ofacSanctionGuide;
      this.hideIFrameHelpButton =
        (data.flags.hideIFrameHelpButton && isAppLoadedInsideIframe()) || false;
      this.imageUrl =
        data?.resources?.controlPanelInvertedLogo || data?.resources?.controlPanelLogoUrl;
      smartStorage.webmail = this.setSafeValues(data, ["urls", "webmail"]);
      if (smartStorage.getItem("webmail") === "undefined") {
        // eslint-disable-next-line no-undef
        smartStorage.webmail = process.env.VUE_APP_OPEN_WEBMAIL_URL;
      }

      const { urls = {}, flags = {}, emails = {} } = data;
      this.$root.partnerURLs = urls;
      this.partnerURLs = { ...urls };
      // `?? true`, not `|| true`: only fall back when the backend omits the
      // field. An explicit `paymentEnabled: false` (partner has payments
      // disabled) must be preserved — `|| true` would clobber it to true.
      this.paymentEnabled = this.setSafeValues(data, ["paymentEnabled"]) ?? true;
      smartStorage.setItem("paymentEnabled", this.paymentEnabled || false);
      this.isTitanMail = flags.rebrandedToTitan;
      this.rebrandedToTitan = flags.rebrandedToTitan;
      this.enableDNSVerification = flags.enableDNSVerification;

      const brandOtherInfo = {
        supportEmail: emails.supportEmail,
        adminGuide: urls.adminGuide,
        billing: {
          termsOfUse: urls.termsOfUse,
          privacyPolicy: urls.privacyPolicy,
        },
      };

      if (this.enableDNSVerification) {
        // dns config is a common API and adding domain connect param to every dns config
        // call could add significant load system since it has external checks involved
        // so better to keep this flag based
        // currently checkDomainConnect would be enabled for godaddy (where forgoPayments flag is set)
        if (flags.forgoPayments) {
          smartStorage.setItem("checkDomainConnect", true);
        }
      }

      this.setBrandDetails(brandOtherInfo);
      setWebsiteMetaData.call(this);
      return Promise.resolve(data);
    },
    onDNSLookupSuccess(data) {
      this.lookupData = data;
      this.setDomainConnectData(data);
      const domainConnectStatus = getCookie("domainConnectStatus");
      const prevDomainConnectStatus = getCookie("prevDomainConnectStatus");
      const currentDomainConnectStatus = `${data.mxVerified}-${data.spfVerified}`;
      const resultChanged =
        domainConnectStatus === "pending" && prevDomainConnectStatus !== currentDomainConnectStatus;
      if (domainConnectStatus !== "pending" || resultChanged) {
        setCookie("domainConnectStatus", "", -1);
        setCookie("prevDomainConnectStatus", "", -1);
      }

      this.setBrandDetails(parseDnsRecords(data.expectedRecords, this.domainName));
      const mxVerified = this.domainMXRecordVerified;
      const spfVerified = this.isDomainSpfVerified;

      if (mxVerified) {
        smartStorage.setItem("showCongratsNote", !!resultChanged);
        setCookie("domainConnectStatus", "", -1);
        setCookie("prevDomainConnectStatus", "", -1);
      } else if (isNeoCustomDomain()) {
        // if neo custom domain mx is not verified then check for entri setup
        this.getEntriSetupDetails();
      }

      this.fetchEntriDetailsIfMissing();

      if (mxVerified && spfVerified) {
        // once the mx and spf verified we dont need to keep
        // domain connect param in dns config api
        smartStorage.setItem("checkDomainConnect", false);
      }
    },
    fetchEntriDetailsIfMissing() {
      if (this.isEntriDetailsFetched) return false;

      const aVerified = this.domainARecordVerified;
      const cNameVerified = this.domainCNameRecordVerified;

      // check for site records
      if (isNeoCustomDomain() && !(aVerified && cNameVerified)) {
        this.getEntriSetupDetails();
      }
    },
    async getEntriSetupDetails() {
      if (!this.domainConnectData?.expectedRecords) return false;

      entriService
        .checkDomain(this.domainName, this.domainConnectData.expectedRecords)
        .then(response => {
          if (process.env.VUE_APP_ENV_FLAG !== "prod") {
            /* Log entri check domain response only on staging and development. */
            Logger.info("Entri response", response);
          }

          this.isEntriDetailsFetched = true;
        });
    },
    async fetchEmailAccounts(products) {
      if (!hasMailSuiteProduct(products || this.products)) return Promise.resolve([]);

      const data = await emailService.getEmailList();

      if (data?.accounts?.length) {
        this.medusaEvents.send(
          this.medusaEvents.createEventObj(
            {
              eventName: "mx_banner_shown",
              source: getAppSource(),
              ...this.getDnsAndEmailMedusaAttrs(),
            },
            this.domainName,
            "flock_order"
          )
        );
      }
      return data;
    },
    onDnsLookupError() {
      this.setDomainConnectData({});
      this.setBrandDetails({
        mxItems: [],
        spfItems: [],
        domainOwnershipItems: [],
      });
      this.hideAppLoader();
    },
    fetchDnsDetails() {
      if (this.enableDNSVerification) {
        this.runDnsLookup(
          data => this.onDNSLookupSuccess(data),
          error => this.onDnsLookupError(error),
          {
            source: "control_panel_banner",
            // Decides whether the lookup reports its `mail_connect_domain_*` or
            // `site_connect_domain_*` events, and which verification state counts as complete.
            // Left unset it always fell through to the mail variant, even on a site-only bundle.
            setupMode: hasMailSuiteProduct(this.products)
              ? ENTRI_SETUP_MODE.EMAIL
              : ENTRI_SETUP_MODE.SITE,
          }
        );
      }
    },
    setBrandDetails(otherInfo) {
      // set current brand and get its details
      brand.setDetails(otherInfo);
      this.brandDetails = brand.getDetails();
      this.brandLogo = this.brandDetails.logo;
    },
    getAllBundles() {
      return bundleService.getBundleList().then(data => {
        if (!data?.bundleDetails?.length) {
          this.showToast(this.$t("toast.noBundlesFound"));
          const error = new Error("no_bundles_found");
          this.forceLogout(error);
          return Promise.reject(error);
        }

        return data;
      });
    },
    hideAppLoader() {
      this.isFetching = false;
      measureInitialAppLoad();
    },
    setMailPlanInfo(currentPlanDetails) {
      smartStorage.plan = currentPlanDetails ? currentPlanDetails.displayName : "";
    },
    fetchPaymentURLDetails(ac) {
      if (isNeoBrand()) {
        // incase of neo, we dont need to make check, its always inAppPayment
        this.$store.dispatch("payment/externalPayment", null);
        return Promise.resolve({ makePaymentUrl: "inAppPayment" });
      }

      const billingActionType =
        this.hideBilling || this.view.openPaymentURLInDiffTab ? "billing" : null;
      const action = billingActionType || ac || "upgradePlan";
      return billingService
        .checkPaymentType(action)
        .then(data => {
          let res = data;
          if (data.makePaymentUrl === "#") {
            this.paymentEnabled = false;
          } else {
            res = { ...data, action };
            this.$store.dispatch(
              "payment/externalPayment",
              data.makePaymentUrl !== "inAppPayment" ? res : null
            );
          }
          return Promise.resolve(res);
        })
        .catch(error => {
          console.error("Could not fetch payment details", error);
          this.$store.dispatch("payment/externalPayment", null);
          return Promise.resolve(null);
        });
    },
    /**
     * Fetches the trial configuration from server.
     * @param addon {Object} - The addon for which configuration is to be fetched.
     */
    async fetchTrialConfig(addon) {
      const data = await this.$http.get("/panel/product/trial-config", {
        params: {
          product: addon.type,
        },
      });
      this.$store.dispatch("trials", {
        [addon.type]: data,
      });
    },
    /**
     * Fetches the trial config for all addons
     * @param addons {Array} - The array of addons obtained from GET /get-all-addons API for this account.
     */
    fetchAddonTrialConfigurations(addons) {
      for (const addonType of ADDONS_ON_TRIAL) {
        const addon = addons.find(a => a.type === addonType);
        if (addon) {
          this.fetchTrialConfig(addon);
        }
      }
    },
    setCurrentBundleDetails(currentPlan) {
      try {
        // now currently plan will be decided from fetchPlans and not from bundleList.
        const currentBundle = this.getCurrentBundleDetails(currentPlan);
        this.setLocale(currentBundle.locale);
        const bundleId = currentBundle?.bundleId;
        bundleId && smartStorage.setItem("bundleId", bundleId);
        this.$store.dispatch("domain/domainName", currentBundle.name);
      } catch (e) {
        this.logout();
      }
    },
    handleInternalRedirection() {
      // `section` is a route name
      const section = smartStorage.getItem("section");
      // `action` is an identifier to decide what to do in that section/route
      const action = getAppAction();
      const actionProps = getAppActionProps();
      if (section) {
        const launchEmail = smartStorage.getItem("launchEmail");
        const params = getFilteredQueryParams(["section"]);

        /**
         * WARN: lets avoid adding query params for internal redirection
         * we can achieve same with `queryParamStore`
         * we don't need to expose/maintain an another action specific params in url
         */
        switch (action) {
          case ROUTE_ACTIONS["LAUNCH-EMAIL-CREATION"]:
          case ROUTE_ACTIONS.LAUNCH_EMAIL_CREATION:
            smartStorage.launchEmailCreation = launchEmail
              ? decodeURIComponent(launchEmail)
              : "true";
            break;
          case ROUTE_ACTIONS.RENEW:
            params.set(FLOW_TYPES.RENEW_BUNDLE, "true");
            break;
          case ROUTE_ACTIONS.RENEW_CARD_PAYMENT:
            queryParamStore.set(ROUTE_ACTIONS_INTERNAL_FLAG.OPEN_RENEW_FLOW_CARD_SECTION, true);
            break;
          case ROUTE_ACTIONS.TRIAL_UPGRADE_WITH_ONE_MONTH_EXT:
            queryParamStore.set(ROUTE_ACTIONS_INTERNAL_FLAG.MONTH_EXTENSION, true);
            queryParamStore.set(ROUTE_ACTIONS_INTERNAL_FLAG.OPEN_RENEW_FLOW_CARD_SECTION, true);
            break;
          case ROUTE_ACTIONS.LAUNCH_HIGHER_PLAN_UPGRADE:
            params.set(ROUTE_ACTIONS.LAUNCH_HIGHER_PLAN_UPGRADE, "true");
            break;
          case ROUTE_ACTIONS.REACTIVATE_SUBSCRIPTION:
            params.set(ROUTE_ACTIONS.REACTIVATE_SUBSCRIPTION, "true");
            break;
          case ROUTE_ACTIONS.SHOW_PLAN_FEATURE:
            params.set(ROUTE_ACTIONS_PROPS.FEATURE_KEY, actionProps);
            break;
          case ROUTE_ACTIONS.SHOW_CHANGE_RENEWAL_DATE_FLOW:
            queryParamStore.set(ROUTE_ACTIONS_INTERNAL_FLAG.SHOW_CHANGE_RENEWAL_DATE_FLOW, true);
            break;
          case ROUTE_ACTIONS.SHOW_MANAGE_CARD_MODAL:
            queryParamStore.set(ROUTE_ACTIONS_INTERNAL_FLAG.SHOW_MANAGE_CARD_MODAL, true);
            break;

          default:
            break;
        }

        this.$router?.replace?.(`/${section}${params ? `?${params.toString()}` : ""}`);
        this.cleanUpRedirectionQueryParams();
      } else if (
        // when authenticated user trying to access non-authenticated route
        // standalone mode
        (/login/i.test(this.$route.name) ||
          // widget mode
          this.$route.meta.view === "widget") &&
        this.$route.name !== this.view.home
      ) {
        this.$router.replace({ name: this.view.home });
      }
    },
    cleanUpRedirectionQueryParams() {
      // smartStorage.removeItems(["section", "action", "launchEmail"]);
      smartStorage.removeItem("section");
      smartStorage.removeItem("action");
      smartStorage.removeItem("launchEmail");
      smartStorage.removeItem("actionProps");
    },
    setIsDiscountedPricingAndTrialInfoInPlans(
      storeKey,
      planList,
      currentBillingCycle,
      productPlanDetails
    ) {
      if (!planList.length || !currentBillingCycle || !productPlanDetails) return [];
      planList.forEach(plan => {
        plan.planPricing.isDiscountedPricing =
          plan.planPricing?.[currentBillingCycle]?.firstBillingCyclePrice <
          plan.planPricing?.[currentBillingCycle]?.price;
      });
      const updatedPlans = addPlanTrialInfo(planList, productPlanDetails);
      this.$store.dispatch(storeKey, updatedPlans);
      return updatedPlans;
    },
    setCurrentPlanDetails(data, products) {
      const _hasMailSuiteProduct = hasMailSuiteProduct(products);
      const _hasSiteProduct = hasSiteProduct(products);
      const _hasDomainProduct = hasDomainProduct(products);

      const mailPlanDetails = _hasMailSuiteProduct
        ? this.getMailPlanDetails(data.mailSuiteDetails.planId)
        : null;
      const sitePlanDetails = _hasSiteProduct
        ? this.getSitePlanDetails(data.siteDetails.planId)
        : null;
      const domainPlanDetails = _hasDomainProduct
        ? this.getDomainPlanDetails(data.domainDetails.planId)
        : null;

      const bundleDetails = this.getCurrentBundleDetails(data);
      const domainDetails =
        _hasDomainProduct && domainPlanDetails
          ? formatDomainDetails(
              this.getDomainPlanDetails(domainPlanDetails.paidPlanId || domainPlanDetails.id),
              domainPlanDetails,
              data.domainDetails,
              bundleDetails
            )
          : null;

      const additionalPlanDetails = {
        isDomainPaid: domainPlanDetails?.isPaid,
        ...(_hasMailSuiteProduct
          ? {
              mailSuiteDetails: formatProductDetails(
                this.getMailPlanDetails(mailPlanDetails.paidPlanId),
                mailPlanDetails,
                data.mailSuiteDetails
              ),
            }
          : {}),
        ...(_hasSiteProduct
          ? {
              siteDetails: formatProductDetails(
                this.getSitePlanDetails(sitePlanDetails.paidPlanId),
                sitePlanDetails,
                data.siteDetails
              ),
            }
          : {}),
        ...(_hasDomainProduct
          ? {
              domainDetails,
            }
          : {}),
      };

      const currentPlanDetails = {
        billingCycle: "yearly", // default
        ...data,
        ...additionalPlanDetails,
        showBillingCycleSwitcher: !data.billingCycle,
      };

      this.$store.dispatch("currentPlanDetails", currentPlanDetails);
      this.$root.currentPlan = currentPlanDetails;
      this.setMailPlanInfo(currentPlanDetails?.mailSuiteDetails?.planDetails);
      this.setIsDiscountedPricingAndTrialInfoInPlans(
        "mail/plans",
        this.mailPlanList,
        currentPlanDetails.billingCycle,
        mailPlanDetails
      );
      this.setIsDiscountedPricingAndTrialInfoInPlans(
        "site/plans",
        this.sitePlanList,
        currentPlanDetails.billingCycle,
        sitePlanDetails
      );
      return currentPlanDetails;
    },
    fetchCurrentPlan() {
      return billingService.getCurrentPlan().catch(e => {
        Logger.error(e);
        if (!getProductsFromCurrentPlan(this.currentPlan)?.length) {
          this.forceLogout(e);
        }
        return Promise.reject(e);
      });
    },
    // kept this `cb` is for existing/old code implementation
    syncCurrentPlan(cb) {
      return this.fetchCurrentPlan()
        .then(data => {
          const products = getProductsFromCurrentPlan(data);
          const updatedData = this.setCurrentPlanDetails(data, products);
          cb?.(updatedData);
          return updatedData;
        })
        .catch(Logger.error);
    },
    fetchAddons() {
      return billingService.getAllAddons().then(data => {
        // success
        this.addons = data.addons || [];
        return this.fetchAddonTrialConfigurations(this.addons);
      });
    },
    async fetchCardDetails() {
      try {
        // do not make get card details request in this case
        if (this.externalPayment) return Promise.resolve(null);

        return await billingService.getCardDetails();
      } catch {
        // don't break flow
        return Promise.resolve(null);
      }
    },
    redirectToLogin() {
      clearAuthSession();
      this.authenticated = false;
      this.$router.push({ name: "Login" });
    },
    async syncCurrentPlanAndProducts(cb) {
      try {
        await this.syncAllProductPlans();
        await this.syncCurrentPlan();
        cb?.();
      } catch (e) {
        Logger.error("Failed to sync current plan and products", e);
      }
    },
    // Events on EventBus
    destroyFetchListeners() {
      eventBus.$off("go-to-login", this.redirectToLogin);
      eventBus.$off("fetchAddons", this.fetchAddons);
      eventBus.$off("fetchCardDetails", this.fetchCardDetails);
      eventBus.$off("pageLoaded", this.pageLoaded);
      eventBus.$off("fetchCurrentPlan", this.syncCurrentPlan);
      eventBus.$off("syncCurrentPlanAndProducts", this.syncCurrentPlanAndProducts);
      eventBus.$off("syncAllProductPlans", this.syncAllProductPlans);
      eventBus.$off("refetch-email-accounts", this.fetchEmailAccounts);
      eventBus.$off("handleEntriSetupRedirection", this.handleEntriSetupRedirection);
    },
    setupListeners() {
      if (isAppLoadedInsideIframe()) {
        window.addEventListener("message", this.onIframeDimensionChange);
      }
      eventBus.$on("go-to-login", this.redirectToLogin);
      eventBus.$on("fetchAddons", this.fetchAddons);
      eventBus.$on("pageLoaded", this.pageLoaded);
      eventBus.$on("fetchCardDetails", this.fetchCardDetails);
      eventBus.$on("fetchCurrentPlan", this.syncCurrentPlan);
      eventBus.$on("syncAllProductPlans", this.syncAllProductPlans);
      eventBus.$on("syncCurrentPlanAndProducts", this.syncCurrentPlanAndProducts);
      eventBus.$on("refetch-email-accounts", this.fetchEmailAccounts);
      eventBus.$on("handleEntriSetupRedirection", this.handleEntriSetupRedirection);
    },
    fetchControlPanelSource() {
      // Fetch Source from which Control panel was loaded
      const availableSources = ["isFromNeo"];
      const currentSource = availableSources.find(sourceHook => this.$route.query[sourceHook]);
      if (currentSource) {
        this[currentSource] = true;
      }
    },
    validateTokenAndInitCP() {
      if (getExternalAuthToken()) {
        this.authenticateExternalToken();
      } else if (getAuthToken()) {
        this.initCP();
      } else {
        this.authenticated = false;
      }
    },
  },
  mounted() {
    this.shouldShowResponsiveLayout = shouldShowResponsiveCP();

    if (isAppLoadedInsideIframe()) {
      observedElementDetails = this.observeChangesInDOM(document.getElementById("app"));
    }
    // this condition gets exce when user is on mobile (and not in iframe) and user is not logged-in
    // and based on showResponsiveLayout flag we show/hide mobile version on CP
    // ideally isInitCPStepsDone gets assigned once the all API fetching is done
    // but this is special case due to neo (where we support mobile version of CP)
    // in titan we show error screen for mobile
    else if (!this.authenticated && this.isMobile) {
      this.hideAppLoader();
    }
  },
  beforeCreate() {
    const domain = getDomainName();
    this.$store.dispatch("domain/domainName", domain);
  },
  created() {
    this.setViewAndTheme();
    this.setupListeners();
    this.fetchControlPanelSource();
    initGtm();
    this.validateTokenAndInitCP();
  },
  beforeDestroy() {
    this.destroyFetchListeners();
    window.removeEventListener("message", this.onIframeDimensionChange);
    if (observedElementDetails.observer) observedElementDetails.observer.disconnect();
  },
  watch: {
    /**
     * Some partners need to know if the number of active/purchased/limit (available) mailboxes on this domain.
     * This is a way to notify them.
     * @param {EmailAccounts} emailAccounts - Consists of the `active`, `limit` and `purchased` mailboxes.
     */
    "currentPlanMailDetails.noOfAccounts": function (emailAccounts, oldEmailAccounts) {
      if (
        hasMailSuiteProduct(this.products) &&
        (emailAccounts?.active !== oldEmailAccounts?.active ||
          emailAccounts?.purchased !== oldEmailAccounts?.purchased ||
          emailAccounts?.limit !== oldEmailAccounts?.limit)
      ) {
        sendPostMessageToParentWindow(EMAIL_ACCOUNTS_CHANGED, emailAccounts);
      }
    },
    domainConnectData() {
      if (!this.siteVerifiedAndConnected || !this.domainVerifiedAndConnected) {
        this.fetchEntriDetailsIfMissing();
      }
    },
  },
};
</script>

<style lang="scss">
html {
  background: var(--primaryBgCol) no-repeat;
}
div,
p,
span,
th,
tr,
input,
li,
p,
h1,
h2,
h3,
h4 {
  color: var(--primaryTextCol);
}
input {
  background: var(--tabBgCol);
  color: var(--primaryTextCol);
}
#app {
  font-family: var(--app-font-type);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  font-size: 14px;
  background: var(--primaryBgCol);
}
.link {
  color: var(--linkBtnCol);
  display: inline-block;
  cursor: pointer;

  &:hover {
    color: var(--linkBtnCol);
    text-decoration: underline;
  }
}

.modal-open {
  overflow: hidden;
}
.warning-validation,
.error-validation {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  position: fixed;
  top: 68px;
  left: 0;
  background: var(--warningBgCol);
  padding: 9px 7px;
  color: var(--genericTextCol);
  z-index: 3;
  font-size: 14px;

  i {
    display: inline-block;
    width: 18px;
    height: 15px;
    background: url("./assets/warning-icon.svg") center no-repeat;
    background-size: contain;
    vertical-align: -2px;
    margin-right: 10px;
  }

  .emoji {
    width: 20px;
    height: 20px;
    margin-right: 4px;
    display: inline-block;
    background: url("./assets/celebration.svg") center no-repeat;
    transform: scale(1.2); // necessary else icons looks very small
    vertical-align: text-bottom;
  }

  span {
    font-weight: 600;
    color: var(--genericTextCol);
    & + div {
      display: inline-block;
    }
  }

  .button {
    padding: 7px 18px;
  }

  .button:hover {
    text-decoration: none;
  }
}
.error-validation {
  border: solid 1px var(--errorSecBordCol);
  background-color: var(--errorSecTextCol);
  i {
    background: url("./assets/warning-icon-red.svg") center no-repeat;
  }

  &.secondary {
    border-color: var(--secondaryErrorBannerBorder);
    background-color: var(--secondaryErrorBannerBg);

    i {
      width: 16px;
      height: 16px;
      background: url("./assets/blue-info-icon.svg") center no-repeat;
      margin-right: 8px;
    }
  }
}
.banner-for-widget div {
  position: static;
}
.increase-padding-container {
  padding-top: 105px !important;
}

.active-now {
  margin-left: 10px !important;
}

.button {
  color: var(--primaryBtnTextCol);
  font-size: 14px;
  font-weight: 600;
  border-radius: 5px;
  background-color: var(--primaryBtnCol);
  padding: 10px 18px;
  border: 0;
  line-height: 150%;
  cursor: pointer;

  &.invalid {
    opacity: 0.5;
    cursor: default;
  }
  &.show-spinner {
    @include button-loader();
  }
  &.show-disabled-spinner {
    @include disabled-button-loader();
  }

  &:focus {
    outline: 0;
  }

  &.btn-danger,
  &.btn-danger:hover,
  &.btn-danger:focus,
  &.btn-danger:active {
    background-color: var(--errorPrimaryTextCol) !important;
    box-shadow: none !important;
    border: none !important;
  }

  &.button__gray {
    background: var(--lineSepaCol);
    border: 1px solid var(--lineSepaCol);
    color: var(--primaryTextCol);
    font-weight: normal;
    &:hover {
      background: var(--lineSepaCol);
    }
  }

  &.button__line-blue {
    background: transparent;
    border: 1px solid var(--transparent-btn);
    color: var(--transparent-btn);

    &:hover {
      background: transparent;
    }
  }

  &.button__transparent {
    background: transparent;
    border: 0;
    padding: 10px 8px;
  }

  &:nth-child(n + 2) {
    margin-left: 16px;
  }

  &:first-of-type {
    margin: 0;
  }
}
.widgetView {
  margin: 30px 0;
  width: 100%;
}
.widgetViewContainer {
  margin-bottom: 20px;
  display: flex;
  justify-content: space-between;
  .help-link-icon {
    width: 66px;
    height: 23px;
    background: url("./assets/help-grey.svg");
  }
  .rightAlignIcon {
    position: absolute;
    right: 0;
  }
}
.powered-by-titan {
  text-align: center;
  margin-top: 30px;
}
.view {
  margin-top: 50px;
  padding: 50px;
  margin-left: 280px;
  background: var(--primaryBgCol);
  min-height: calc(100vh - 68px);
  width: calc(100% - 280px);
}

.card-container {
  border-radius: 5px;
  background-color: var(--tabBgCol);
  padding: 24px;
  border: 1px solid var(--lineSepaCol);
  margin-top: 30px;
  header {
    font-size: 16px;
  }
}

h4.view-heading {
  font-size: 20px;
  font-weight: 600;
}

.card-heading {
  font-size: 16px;
  font-weight: 600;
}

.card-subheading {
  color: var(--secondaryTextCol);
}

/*commons*/
h1,
h2,
h3,
h4,
h5,
h6 {
  font-weight: 600 !important;
}

.input-wrapper {
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }
}

.input-addon-wrapper {
  position: relative;

  .password-hidden,
  .password-shown {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
  }

  .input {
    padding: 9px 55% 9px 15px;
  }
}

.color-grey {
  color: var(--textBtnCol);
}

.color-blue {
  color: var(--primaryBtnCol);
}

.addon {
  position: absolute;
  right: 1px;
  top: 50%;
  transform: translateY(-50%);
  background: var(--readOnlyTxtBgCol);
  display: inline-block;
  padding: 9px;
  border-radius: 0 4px 4px 0;
  border-left: 1px solid var(--disableCol);
  line-height: normal;
  width: 50%;
  height: calc(100% - 3px);
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  color: var(--primaryTextCol);
}

.input {
  border-radius: 4px;
  background-color: var(--tabBgCol);
  border: 1px solid var(--inputBorderCol);
  padding: 9px 15px;
  line-height: 20px;
  width: 100%;

  &[readonly="readonly"] {
    color: var(--secondaryTextCol);
    background-color: var(--readOnlyTxtBgCol);
  }

  &.has-error {
    border: 1px solid var(--errorPrimaryTextCol);
  }

  &:focus {
    border: 1px solid var(--inputFocusBorderCol);
    outline: 0;
  }
}

.error {
  color: var(--errorPrimaryTextCol);
  font-size: 12px;
  letter-spacing: normal;
}

.input.password.password-hidden {
  background: url("~@/assets/password-toggle-on.svg") center right no-repeat / contain;
}

.input.password.password-shown {
  background: url("~@/assets/password-toggle-off.svg");
}

.input::placeholder {
  color: var(--inputBorderCol);
}

p {
  line-height: 18px;
  color: var(--primaryTextCol);
  font-size: 14px;
}

div.toasted-container {
  z-index: 10;
  .toasted.toasted-primary {
    min-width: 300px;
    border-radius: 4px;
    background: var(--primaryTextCol);
    color: var(--tabBgCol);
    padding: 19px 24px;
    font-size: 15px;
    font-weight: 600;
    font-style: normal;
    line-height: 1.33;

    .ripple.action {
      text-decoration: none;
      color: var(--tabBgCol);
      font-size: 20px;
      padding: 0;
      margin: 0 0 0 20px;
      font-weight: normal;

      &:hover {
        color: var(--tabBgCol);
      }
    }
  }
}

.v-context.overflow-context-menu {
  background-color: var(--tabBgCol) !important;
  box-shadow: 0 0 8px -1px var(--boxShadowCol7) !important;
  width: 215px !important;
  right: -20px !important;
  bottom: -27px !important;
  position: absolute !important;
  ul {
    li {
      padding: 8px 24px !important;
      font-size: 14px;
      font-weight: 400;
      color: var(--primaryTextCol);
      text-align: left;

      &:hover {
        background: var(--readOnlyTxtBgCol) !important;
        color: var(--primaryTextCol) !important;
      }

      &:last-of-type,
      &:first-of-type {
        margin: 0 !important;
      }
    }
  }
}
::-webkit-scrollbar {
  width: 5px;
  height: 5px;
}
.without-scrollbar::-webkit-scrollbar {
  display: none; /* Hide scrollbar for Chrome, Safari and Opera */
}
.without-scrollbar {
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
}
::-webkit-scrollbar-thumb {
  border-radius: 30px;
  -webkit-box-shadow: inset 0 0 6px var(--boxShadowCol1);
}

.vue-tooltip {
  background: var(--primaryTextCol) !important;
  font-size: 12px !important;

  .tooltip-arrow {
    border-top-color: var(--primaryTextCol) !important;
  }
}

.search-container {
  text-align: right;

  .search-wrapper {
    position: relative;
    margin-right: 16px;

    .input {
      max-width: 290px;
      background: url("./assets/search-small.svg") no-repeat no-repeat 10px center/16px;
      padding-left: 30px;
    }

    .clear-search {
      width: 15px;
      height: 18px;
      background: url("./assets/cross.svg") center no-repeat / contain;
      display: inline-block;
      position: absolute;
      top: 50%;
      right: 10px;
      transform: translateY(-50%);
      cursor: pointer;
    }
  }
}

/*Tags input CSS*/
.vue-tags-input {
  .ti-input {
    border-radius: 4px;
    background-color: var(--tabBgCol);
    border: 1px solid var(--inputBorderCol) !important;
    padding: 8px 15px !important;
    line-height: 20px;
    width: var(--inputBoxWidth);
    max-height: 250px;
    overflow: auto;

    &:focus {
      border: 1px solid var(--inputFocusBorderCol) !important;
      outline: 0 !important;
    }

    .ti-tag {
      user-select: none;
      margin: 4px !important;

      &:first-of-type {
        margin-left: 0 !important;
      }

      &:last-of-type {
        margin-right: 8px !important;
      }

      &.input-tag {
        background: var(--tabBgCol);
        color: var(--primaryTextCol);
        border: 1px solid var(--lineSepaCol);
        border-radius: 11px;
        padding: 4px 8px;
        font-weight: 500;
        font-size: 14px;
        /*margin-bottom: 3px !important;*/
        /*margin-top: 3px !important;*/
        margin: 3px 8px 3px 0 !important;
        word-break: break-all;

        &.ti-deletion-mark {
          background: var(--readOnlyTxtBgCol) !important;
        }
      }

      .ti-actions {
        margin-left: 8px !important;
        font-size: 12px !important;
        color: var(--secondaryTextCol) !important;
      }
    }

    .ti-new-tag-input-wrapper {
      padding: 0 !important;
      margin: 0 !important;
      background: var(--tabBgCol);

      input {
        font-size: 14px;
        line-height: 22px;
        caret-color: var(--primaryBtnCol);
        margin: 3px;
      }
    }
  }

  .ti-autocomplete {
    border-radius: 4px;
    box-shadow: 0 0 2px 2px var(--boxShadowCol2);
    background-color: var(--tabBgCol);
    border: 0 !important;
    margin-bottom: 30px;
    color: var(--primaryTextCol) !important;
    max-height: 200px;
    overflow: auto;

    .ti-item {
      padding: 9px 15px !important;
      background-color: var(--tabBgCol) !important;
      &.ti-selected-item {
        color: var(--primaryTextCol) !important;
      }

      & > div {
        padding: 0 !important;
      }
      &:hover {
        background-color: var(--dropdownItemBgCol) !important;
        div {
          color: var(--genericTextCol) !important;
        }
      }
    }

    .no-forwarder-email {
      padding: 12px 14px;
      background: var(--readOnlyTxtBgCol);

      p {
        margin: 0;
      }

      small {
        font-size: 12px;
      }
    }
  }

  &.ti-focus {
    .ti-input {
      border: 1px solid var(--inputFocusBorderCol) !important;
    }
  }
}
.main {
  display: flex;
}

.non-iframe-widget {
  padding: 40px;
}

.modal-overlay.iframe-mode {
  background: unset;
  padding-left: 0;
  position: unset;
  text-align: left;
  justify-content: flex-start;
  .modal-body-container {
    background: unset;
    text-align: left;
    padding-left: 0;
    box-shadow: unset;
    background: unset;
  }
}
.btn:hover {
  color: unset;
}

.no-scroll {
  overflow: hidden;
}

.font-w-500 {
  font-weight: 500;
}

@import "styles/variables";
@media screen and (max-width: $responsive-breakpoint) {
  .main {
    justify-content: center;
    display: block;
  }

  .view {
    width: 100%;
    padding: 0 16px;
    margin: 0;
  }
}
</style>
